import { useCallback, useEffect, useRef } from "react";

interface UseAutoscrollOptions {
  isMessageIncoming: boolean;
}

// Owns the messages-list scroll behavior: when active, scrolls to bottom every
// 100ms; when the user scrolls up it pauses, and resumes if they scroll back
// to the bottom. The previous in-component implementation re-attached a scroll
// listener on every beginAutoscroll call, leaking listeners forever — this
// version attaches the listener exactly once.
export const useAutoscroll = ({ isMessageIncoming }: UseAutoscrollOptions) => {
  const messagesListRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<number | null>(null);
  const scrollListenerAttachedRef = useRef(false);
  const isMessageIncomingRef = useRef(isMessageIncoming);

  // Mirror the prop into a ref so the one-time scroll listener can read the
  // latest value without re-binding. Previously the listener happened to stay
  // up-to-date because it was re-attached every call (which is the bug).
  useEffect(() => {
    isMessageIncomingRef.current = isMessageIncoming;
  }, [isMessageIncoming]);

  const beginAutoscroll = useCallback(() => {
    const list = messagesListRef.current;
    if (!list) return;

    list.scrollTo(0, list.scrollHeight);

    if (scrollIntervalRef.current) return;

    scrollIntervalRef.current = window.setInterval(() => {
      if (messagesListRef.current) {
        messagesListRef.current.scrollTo(
          0,
          messagesListRef.current.scrollHeight,
        );
      }
    }, 100);

    if (scrollListenerAttachedRef.current) return;
    scrollListenerAttachedRef.current = true;

    list.addEventListener("scroll", () => {
      const current = messagesListRef.current;
      if (!current) return;

      // Pause if the user scrolled up and no message is mid-stream.
      if (
        !isMessageIncomingRef.current &&
        current.scrollTop < current.scrollHeight - current.clientHeight
      ) {
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }

      // Resume if the user scrolled back to the bottom.
      if (current.scrollTop === current.scrollHeight - current.clientHeight) {
        beginAutoscroll();
      }
    });
  }, []);

  // Restart autoscroll when a new message starts streaming, in case the user
  // had paused it.
  useEffect(() => {
    if (isMessageIncoming && !scrollIntervalRef.current) {
      beginAutoscroll();
    }
  }, [isMessageIncoming, beginAutoscroll]);

  // Stop the interval on unmount. The listener is left in place — the element
  // is being torn down with the component anyway.
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, []);

  return { messagesListRef, beginAutoscroll };
};
